// src/components/municipalities/MunicipalityCard.ts
import "./MunicipalityCard.css";
import type { Municipality } from "../../types/Municipality";

/* ============================================================
   Auto-flag loader (Japan only)
============================================================ */
function getFlagUrl(id: string): string | null {
  const prefix = id.split("-")[0]; // "jpn"
  if (prefix === "jpn") return "https://flagcdn.com/jp.svg";
  return null;
}

/* ============================================================
   Main Renderer — returns HTML string
============================================================ */
export function renderMunicipalityCard(m: Municipality): string {
  const slug = m.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  const prefectureSlug = m.prefecture.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  const flagUrl = getFlagUrl(m.id);

  const questions = [
    {
      q: `What is ${m.name} known for?`,
      a: `${m.name} is known for its culture, attractions, and local history.`,
    },
    {
      q: `Is ${m.name} safe to visit?`,
      a: `Safety varies, but major areas and tourist spots are generally safe.`,
    },
    {
      q: `Where is ${m.name} located?`,
      a: `${m.name} is located in ${m.prefecture}, ${m.region} region.`,
    },
    {
      q: `What is the best time to visit ${m.name}?`,
      a: `The best time to visit depends on weather, festivals, and travel plans.`,
    },
  ];

  const paaHTML = questions
    .map(
      (item, i) => `
      <div class="paa-item" data-index="${i}">
        <div class="paa-question">
          <span>${item.q}</span>
          <span class="paa-arrow">▼</span>
        </div>
        <div class="paa-answer" style="display:none;">
          <p>${item.a}</p>
        </div>
      </div>
    `,
    )
    .join("");

  return `
    <div class="municipality-card-full">

      <!-- Header -->
      <div class="municipality-header">
        <h1>${m.name}</h1>
        <p class="subtext">
          ${m.type} • ${m.prefecture}, ${m.region}
        </p>
      </div>

      <!-- Two images -->
      <div class="municipality-image-row">

        ${
          flagUrl
            ? `<img src="${flagUrl}" class="bubble-img" />`
            : `
          <img
            src="/static/vite/ayesearch/municipalities/japan/${prefectureSlug}/${slug}/hero.png"
            class="bubble-img muni-fallback"
            data-fallback="https://source.unsplash.com/featured/?${m.name}"
          />`
        }

        <img
          src="/static/vite/ayesearch/municipalities/japan/${prefectureSlug}/${slug}/alt.png"
          class="bubble-img muni-fallback"
          data-fallback="https://source.unsplash.com/featured/?${m.name} city"
        />
      </div>

      <!-- Main section -->
      <div class="municipality-main-section">

        <!-- Info grid -->
        <div class="info-grid">
          <div class="info-bubble">
            <h4>Type</h4>
            <p>${m.type}</p>
          </div>

          <div class="info-bubble">
            <h4>Prefecture</h4>
            <p>${m.prefecture}</p>
          </div>

          <div class="info-bubble">
            <h4>Region</h4>
            <p>${m.region}</p>
          </div>

          <div class="info-bubble">
            <h4>Coordinates</h4>
            <p>${m.lat ?? "N/A"}, ${m.lng ?? "N/A"}</p>
          </div>
        </div>

        <!-- About -->
        <div class="about-card">
          <h3>About ${m.name}</h3>
          <p>
            ${m.name} is a ${m.type} located in ${m.prefecture}, part of the
            ${m.region} region of Japan. It is known for its culture,
            attractions, and unique character.
          </p>
        </div>
      </div>

      <hr class="divider" />

      <!-- Bottom 2-column layout -->
      <div class="municipality-bottom-grid">

        <!-- LEFT COLUMN -->
        <div class="left-col">

          <!-- Wikipedia -->
          <div class="wiki-card">
            <img src="/static/icons/wiki.png" class="wiki-icon" />
            <div>
              <h4>${m.name} — Wikipedia</h4>
              <p>Learn more about ${m.name} and its history, attractions, and culture…</p>
              <a href="https://en.wikipedia.org/wiki/${m.name}" target="_blank">Read more</a>
            </div>
          </div>

          <!-- Aikipedia -->
          <div class="wiki-card">
            <img src="/static/icons/wiki.png" class="wiki-icon" />
            <div>
              <h4>${m.name} — Aikipedia</h4>
              <p>AI‑generated encyclopedia summary for ${m.name}…</p>
              <a href="https://aikipedia.org/${slug}" target="_blank">Explore Aikipedia</a>
            </div>
          </div>

          <!-- People Also Ask -->
          <div class="wiki-card paa-card">
            <h4>People Also Ask</h4>
            ${paaHTML}
          </div>
        </div>

        <!-- RIGHT COLUMN -->
        <div class="right-col">

          <!-- Landmark -->
          <div class="place-card">
            <img
              src="/static/vite/ayesearch/municipalities/japan/${prefectureSlug}/${slug}/landmark.png"
              class="place-img muni-fallback"
              data-fallback="https://source.unsplash.com/featured/?${m.name} landmark"
            />
            <div class="place-info">
              <h4>Top Landmark</h4>
              <p>Explore iconic sights in ${m.name}.</p>
            </div>
          </div>

          <!-- Destination -->
          <div class="place-card">
            <img
              src="/static/vite/ayesearch/municipalities/japan/${prefectureSlug}/${slug}/destination.png"
              class="place-img muni-fallback"
              data-fallback="https://source.unsplash.com/featured/?${m.name} tourism"
            />
            <div class="place-info">
              <h4>Popular Destination</h4>
              <p>Discover must‑visit spots.</p>
            </div>
          </div>

          <!-- Hidden Gem -->
          <div class="place-card">
            <img
              src="/static/vite/ayesearch/municipalities/japan/${prefectureSlug}/${slug}/gem.png"
              class="place-img muni-fallback"
              data-fallback="https://source.unsplash.com/featured/?${m.name} hidden gem"
            />
            <div class="place-info">
              <h4>Hidden Gem</h4>
              <p>Find unique places off the path.</p>
            </div>
          </div>

        </div>
      </div>
    </div>
  `;
}

/* ============================================================
   Event Initializer — accordion + image fallbacks
============================================================ */
export function initMunicipalityCardEvents(container: HTMLElement): void {
  // Accordion
  const items = container.querySelectorAll(".paa-item");
  items.forEach((item) => {
    item.addEventListener("click", () => {
      const answer = item.querySelector(".paa-answer") as HTMLElement;
      const arrow = item.querySelector(".paa-arrow") as HTMLElement;

      const isOpen = answer.style.display === "block";
      answer.style.display = isOpen ? "none" : "block";
      arrow.textContent = isOpen ? "▼" : "▲";
    });
  });

  // Image fallbacks
  const imgs = container.querySelectorAll(".muni-fallback");
  imgs.forEach((img) => {
    img.addEventListener("error", () => {
      const fallback = img.getAttribute("data-fallback");
      if (fallback) img.setAttribute("src", fallback);
    });
  });
}
